import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { useUsdxlEmaOracle } from '@/domain/oracles/usdxlEmaOracle'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { BaseUnitNumber, NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { getAddressBySymbol, TokenSymbolEnum } from '@/utils/tokens'
import { useChainId } from 'wagmi'
import { useGsmBalance } from './useGsmBalance'

export interface UseSummaryInfoResults {
  isLoading: boolean
  usdxlPrice: NormalizedUnitNumber
  totalSupply: NormalizedUnitNumber
  borrowAPY: Percentage
  borrowPoints?: number
  reserveBalanceUSD?: NormalizedUnitNumber
}

export function useSummaryInfo(): UseSummaryInfoResults {
  const chainId = useChainId()
  const { marketInfo } = useMarketInfo({ chainId })

  const gsmTokenAddress = CheckedAddress(getAddressBySymbol(TokenSymbolEnum.USD_T0))
  const usdxlAddress = CheckedAddress(getAddressBySymbol(TokenSymbolEnum.USDXL))
  const reserveToken = marketInfo.findOneReserveByUnderlyingAsset(gsmTokenAddress).token
  const usdxlReserve = marketInfo.findOneReserveByUnderlyingAsset(usdxlAddress)

  const { data: usdxlEmaOracleData } = useUsdxlEmaOracle({ chainId })
  const { getByAddress, isLoading: isPointsDataLoading } = usePointsData()

  const {
    data: { balance },
  } = useGsmBalance({ chainId })

  const borrowPoints = getByAddress({
    type: 'pooled',
    action: 'borrow',
    address: usdxlReserve.token.address,
  })

  const reserveBalanceUSD = reserveToken.toUSD(NormalizedUnitNumber(reserveToken.fromBaseUnit(BaseUnitNumber(balance))))

  return {
    isLoading: isPointsDataLoading,
    usdxlPrice: usdxlEmaOracleData.formattedPrice,
    totalSupply: marketInfo.facilitator.totalSupply,
    borrowAPY: Percentage(usdxlReserve.variableBorrowApy ?? 0, true, true),
    borrowPoints: borrowPoints?.multiplier,
    reserveBalanceUSD,
  }
}
